import { useState, useEffect } from "react";
import { Sparkles, MessageCircle, X, Zap } from "lucide-react";
import { whatsappLink } from "@/lib/config";

const WhatsAppIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.015-.53-.015-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.017 2.264v.1c-.015.966.41 1.873.996 2.606 1.34 1.676 3 3.157 4.942 4.007.545.237 2.085.982 2.678.982.539 0 1.428-.213 1.747-.543.288-.302.53-.67.53-1.085 0-.1-.015-.186-.03-.258-.087-.302-1.447-.992-1.717-.992m-2.953 5.837a8.998 8.998 0 0 1-4.685-1.288L10.5 21.07l-3.15.988.985-3.073-.23-.358a9.03 9.03 0 0 1-1.415-4.85c0-4.986 4.07-9.054 9.057-9.054 2.413 0 4.68.95 6.38 2.64a8.98 8.98 0 0 1 2.65 6.426c0 5-4.067 9.055-9.05 9.055m0-19.943c-6 0-10.88 4.876-10.88 10.87 0 1.92.502 3.792 1.462 5.425L5.2 27.5l5.373-1.408a10.89 10.89 0 0 0 5.2 1.325c6.003 0 10.88-4.876 10.88-10.87 0-2.903-1.132-5.63-3.188-7.685a10.843 10.843 0 0 0-7.68-3.19" />
  </svg>
);

export default function FloatingMenu({ onOpenChat, chatOpen }) {
  const [expanded, setExpanded] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const m = setTimeout(() => setMounted(true), 1200);
    const s = setTimeout(() => setShowHint(true), 3500);
    const h = setTimeout(() => setShowHint(false), 10000);
    return () => {
      clearTimeout(m);
      clearTimeout(s);
      clearTimeout(h);
    };
  }, []);

  // Hide the whole FAB while chat panel is open (chat has its own close button)
  if (!mounted || chatOpen) return null;

  const handleChat = () => {
    setExpanded(false);
    setShowHint(false);
    onOpenChat();
  };

  const handleWhatsApp = () => {
    setExpanded(false);
    setShowHint(false);
    window.open(whatsappLink(), "_blank");
  };

  return (
    <div className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-50 flex flex-col items-end gap-3">
      {/* Expanded options */}
      {expanded && (
        <>
          {/* AI Chat option */}
          <button
            data-testid="fab-chat-ia-btn"
            onClick={handleChat}
            className="animate-slide-up flex items-center gap-3 group"
          >
            <div className="bg-[#0A0A0A]/95 backdrop-blur-xl border border-[#D4AF37]/40 shadow-[0_8px_24px_rgba(0,0,0,0.4)] pl-4 pr-5 py-3 flex items-center gap-1.5">
              <span className="text-xs tracking-[0.15em] uppercase text-[#D4AF37] font-semibold">
                Chat com IA
              </span>
              <Zap size={12} strokeWidth={2} className="text-[#D4AF37]" />
            </div>
            <span className="flex-shrink-0 w-14 h-14 bg-[#D4AF37] text-[#050505] flex items-center justify-center rounded-full shadow-[0_8px_24px_rgba(212,175,55,0.4)] group-hover:-translate-y-1 transition-transform">
              <Sparkles size={22} strokeWidth={1.75} />
            </span>
          </button>

          {/* WhatsApp option */}
          <button
            data-testid="fab-whatsapp-btn"
            onClick={handleWhatsApp}
            className="animate-slide-up flex items-center gap-3 group"
            style={{ animationDelay: "60ms" }}
          >
            <div className="bg-[#0A0A0A]/95 backdrop-blur-xl border border-[#25D366]/40 shadow-[0_8px_24px_rgba(0,0,0,0.4)] pl-4 pr-5 py-3 flex items-center gap-1.5">
              <span className="text-xs tracking-[0.15em] uppercase text-[#25D366] font-semibold">
                WhatsApp
              </span>
              <MessageCircle size={12} strokeWidth={2} className="text-[#25D366]" />
            </div>
            <span className="flex-shrink-0 w-14 h-14 bg-[#25D366] text-white flex items-center justify-center rounded-full shadow-[0_8px_24px_rgba(37,211,102,0.4)] group-hover:-translate-y-1 transition-transform">
              <WhatsAppIcon size={24} />
            </span>
          </button>
        </>
      )}

      {/* Hint tooltip (only when collapsed) */}
      {!expanded && showHint && (
        <div
          data-testid="fab-hint"
          className="animate-slide-up bg-[#0A0A0A]/95 backdrop-blur-xl border border-[#D4AF37]/30 text-white/90 text-sm px-4 py-3 max-w-[260px] shadow-[0_8px_24px_rgba(212,175,55,0.15)] mb-1"
        >
          <p className="font-light leading-snug">
            Oi! 💛 Quer falar com a gente? <span className="text-[#D4AF37]">Toque aqui</span>
          </p>
        </div>
      )}

      {/* Primary FAB */}
      <button
        data-testid="fab-main-btn"
        onClick={() => setExpanded(!expanded)}
        aria-label={expanded ? "Fechar menu" : "Falar com a EZ Capelli"}
        className={`relative w-16 h-16 flex items-center justify-center rounded-full shadow-[0_8px_32px_rgba(212,175,55,0.4)] transition-all duration-300 hover:-translate-y-1 ${
          expanded
            ? "bg-[#0A0A0A] border border-[#D4AF37]/50 text-[#D4AF37]"
            : "bg-[#D4AF37] text-[#050505] animate-pulse-gold"
        }`}
      >
        {expanded ? (
          <X size={26} strokeWidth={1.5} />
        ) : (
          <>
            <MessageCircle size={26} strokeWidth={1.75} />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#25D366] rounded-full border-2 border-[#050505]" />
          </>
        )}
      </button>
    </div>
  );
}
